import React, { FC, ReactElement, useEffect, useState } from 'react';

import classNames from 'classnames';

import { ILinkMetadata, ILinkPreviewCardProps } from './linkPreviewCard.interface';
import styles from './linkPreviewCard.module.scss';

import { reportError } from 'lib/reportError';
import { ArrowTopRight, Text } from 'views/components/UI';

const extractDomain = (urlString: string): string => {
  try {
    const urlObj = new URL(urlString);
    return urlObj.hostname.replace('www.', '');
  } catch {
    return urlString;
  }
};

const LinkPreviewCard: FC<ILinkPreviewCardProps> = (props): ReactElement => {
  const { url, className, navigateOnCardClick = true, compact = false } = props;
  const [metadata, setMetadata] = useState<ILinkMetadata | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isCancelled = false;

    const fetchMetadata = async (): Promise<void> => {
      try {
        setLoading(true);

        // Use our own Vercel serverless function
        const apiUrl = `/api/link-preview?url=${encodeURIComponent(url)}`;

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`API returned ${response.status}`);
        }

        const data = await response.json();

        if (isCancelled) return;

        if (data.status === 'success' && data.data) {
          setMetadata({
            title: data.data.title || extractDomain(url),
            description: data.data.description || undefined,
            image: data.data.image || undefined,
            publisher: data.data.siteName || undefined,
            logo: data.data.favicon || undefined,
          });
        } else {
          // Fallback to basic metadata if API doesn't return success
          setMetadata({
            title: extractDomain(url),
          });
        }
      } catch (error) {
        if (isCancelled) return;

        // Fallback to basic metadata on any error
        if (process.env.NODE_ENV === 'development') {
          reportError(error, 'LinkPreviewCard.fetchMetadata');
        }
        setMetadata({
          title: extractDomain(url),
        });
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchMetadata();

    return () => {
      isCancelled = true;
    };
  }, [url]);

  const handleCardClick = (): void => {
    if (navigateOnCardClick) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCardKeyDown = (event: React.KeyboardEvent): void => {
    if (navigateOnCardClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      handleCardClick();
    }
  };

  const handleLinkNavigate = (event: React.MouseEvent | React.KeyboardEvent): void => {
    event.stopPropagation();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const cardClassNames = classNames(styles.linkPreviewCard, className, {
    [styles['linkPreviewCard--static']]: !navigateOnCardClick,
    [styles['linkPreviewCard--compact']]: compact,
  });

  if (loading) {
    return (
      <div className={`${cardClassNames} ${styles.linkPreviewCard__loading}`}>
        <div className={styles.linkPreviewCard__content}>
          <div
            className={styles.linkPreviewCard__skeleton}
            style={{ width: '60%', height: compact ? '14px' : '20px' }}
          />
          {!compact && (
            <>
              <div
                className={styles.linkPreviewCard__skeleton}
                style={{ width: '80%', height: '14px', marginTop: '8px' }}
              />
              <div
                className={styles.linkPreviewCard__skeleton}
                style={{ width: '30%', height: '12px', marginTop: '8px' }}
              />
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cardClassNames}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role={navigateOnCardClick ? 'link' : 'group'}
      {...(navigateOnCardClick ? { tabIndex: 0 } : {})}
    >
      <div className={styles.linkPreviewCard__content}>
        {metadata?.logo && (
          <img
            src={metadata.logo}
            alt={metadata.publisher || ''}
            className={styles.linkPreviewCard__logo}
            onError={e => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        )}
        <div className={styles.linkPreviewCard__header}>
          <h4 className={styles.linkPreviewCard__title}>{metadata?.title || url}</h4>
          <button
            type="button"
            aria-label="open link preview"
            className={styles.linkPreviewCard__action}
            onClick={handleLinkNavigate}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                handleLinkNavigate(event);
              }
            }}
          >
            <ArrowTopRight size="16px" color="var(--sk-icon-brand)" />
          </button>
        </div>
        {!compact && metadata?.description && metadata.description.trim() && (
          <Text className={styles.linkPreviewCard__description}>
            {metadata.description}
          </Text>
        )}
        <div className={styles.linkPreviewCard__meta}>
          {!compact && metadata?.publisher && (
            <Text className={styles.linkPreviewCard__metaText}>{metadata.publisher}</Text>
          )}
          <Text className={styles.linkPreviewCard__url}>{extractDomain(url)}</Text>
        </div>
      </div>
      {!compact && metadata?.image && (
        <div className={styles.linkPreviewCard__imageWrapper}>
          <img
            src={metadata.image}
            alt={metadata.title}
            className={styles.linkPreviewCard__image}
            onError={e => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
      )}
    </div>
  );
};

export { LinkPreviewCard };
